import i18n from "../i18n";

/**
 * Turns a 403 from the API into something a user can act on.
 *
 * Every write endpoint is gated by `PermissionAuthorize(ModuleEnum.x,
 * Permission.y)` on the backend, and a denied call comes back as a bare 403
 * with no body — so before this, a user who lacked a permission saw either
 * nothing at all or a generic "Failed to …" that read like a bug. The URL is
 * the only description of the attempt we get, and it is always shaped
 * `/{module}/{action}`, so the message is derived from that.
 */

// URL segment → key under common:permissions.modules / .actions, so the
// sentence is written in the current language.
const MODULE_LABELS: Record<string, string> = {
  department: "departments",
  businessunit: "businessUnits",
  contract: "contracts",
  member: "staffMembers",
  staff: "staffMembers",
  role: "roles",
  group: "groups",
  template: "templates",
  obligation: "obligations",
  receivable: "receivables",
  party: "parties",
  dashboard: "theDashboard",
  notification: "notifications",
};

const ACTION_LABELS: Record<string, string> = {
  create: "create",
  edit: "edit",
  update: "edit",
  remove: "delete",
  delete: "delete",
  search: "view",
  activate: "activate",
  deactivate: "deactivate",
  uploadmedia: "uploadFilesTo",
  initupload: "uploadFilesTo",
  changestatus: "changeTheStatusOf",
  approve: "approve",
  reject: "reject",
  publish: "publish",
  revert: "revert",
  assign: "assign",
  unassign: "unassign",
  completereview: "completeTheReviewOf",
  updatecaf: "updateTheCafOf",
  sendforapproval: "sendForApproval",
  sendtoreviewer: "sendToAReviewer",
  sendforexternalreview: "sendForExternalReview",
  sendtoeditor: "sendToAnEditor",
  sendtorepository: "sendToTheRepository",
  sendforsignature: "sendForSignature",
  generatecertificate: "generateCertificatesFor",
};

export const isPermissionError = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 403;

/** Builds the user-facing sentence for a denied request to `url`. */
export const describePermissionDenial = (url?: string): string => {
  const path = (url ?? "").split("?")[0].replace(/\/+$/, "");
  const segments = path.split("/").filter(Boolean);

  const actionKey = ACTION_LABELS[(segments.pop() ?? "").toLowerCase()];
  const moduleKey = MODULE_LABELS[(segments.pop() ?? "").toLowerCase()];

  const suffix = " " + i18n.t("common:permissions.contactAdmin");

  if (!actionKey || !moduleKey) {
    return i18n.t("common:permissions.deniedGeneric") + suffix;
  }

  return (
    i18n.t("common:permissions.denied", {
      action: i18n.t(`common:permissions.actions.${actionKey}`),
      module: i18n.t(`common:permissions.modules.${moduleKey}`),
    }) + suffix
  );
};

/**
 * Some screens probe endpoints they know a given role may not hold — the
 * dashboard fans out across modules the user may not have, and the org
 * switcher reloads scoped data — and treat the 403 as an expected, already
 * handled outcome. Wrapping those calls keeps the global toast quiet without
 * threading a per-request flag through every API wrapper.
 */
let suppressionDepth = 0;

export const permissionToastSuppressed = (): boolean => suppressionDepth > 0;

export async function withoutPermissionToast<T>(
  fn: () => Promise<T>,
): Promise<T> {
  suppressionDepth += 1;
  try {
    return await fn();
  } finally {
    suppressionDepth -= 1;
  }
}

/**
 * A single click can fan out into several denied calls (a detail page loads
 * its tabs in parallel); showing the same sentence five times is noise.
 */
const recentlyShown = new Map<string, number>();
const DEDUPE_WINDOW_MS = 4000;

export const shouldShowPermissionToast = (message: string): boolean => {
  const now = Date.now();

  for (const [key, at] of recentlyShown) {
    if (now - at > DEDUPE_WINDOW_MS) recentlyShown.delete(key);
  }

  if (recentlyShown.has(message)) return false;

  recentlyShown.set(message, now);
  return true;
};

/**
 * A denied request is usually also reported by whichever slice or component
 * made the call, with its own generic wording ("Failed to activate
 * department") — that message reads like a server fault and hides the real
 * reason. There are ~200 such catch blocks, so rather than teaching each one
 * about permissions, the specific message wins and generic error toasts
 * raised in its immediate wake are dropped.
 *
 * The window is deliberately short: it only has to cover the catch block that
 * runs when the rejected promise unwinds.
 */
const GENERIC_SUPPRESSION_MS = 1500;

let lastDenialAt = 0;

export const notePermissionDenialShown = (): void => {
  lastDenialAt = Date.now();
};

export const genericErrorSuppressed = (): boolean =>
  Date.now() - lastDenialAt < GENERIC_SUPPRESSION_MS;
